import React, { useState, useEffect, useContext } from 'react';
import { Link, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import StyledMobileMenuUniversal from './MobileMenuUniversal.styled';
import StyledMobileMenuUniversalButton from './MobileMenuUniversalButton.styled';
import SVGIcon from '../../utility/SVGIcon';
import MobileMainMenuCardUniversal from '../../MobileMainMenuCardUniversal/MobileMainMenuCardUniversal';
import { authActions } from '../../../actions/index';
import { ThemePramsContext } from '../../../App';

/**
 * One mobile menu for every marketplace. The markup is the rebud one
 * (MobileMenu); the cards, icons, fonts, section titles and links come from
 * headerConfig.mobileMenuConfig of the selected market config.
 */
const MobileMenuUniversal = React.memo((props) => {
  const { featuredBrand, categories, personality, discoversAll, dispatch, storeInfo, pickupLocations = [], menuAvailability } = props;
  const [clicked, setClicked] = useState(false);

  const { headerConfig = {} } = useContext(ThemePramsContext);
  const menuConfig = headerConfig.mobileMenuConfig || {};
  const {
    icons = {},
    classNames = {},
    thc = [],
    sections = {},
    shopAll = {},
    links = [],
    linkWrap = [],
    accountLinks = {},
    footer = {},
    styling = {},
    card = {},
  } = menuConfig;

  const cardProps = {
    classNames: card.classNames,
    icons: card.icons,
    trailingLink: card.trailingLink,
    styling: card.styling,
  };

  // Wait for /stores/info before drawing the menu, otherwise the seo links show
  // up for a moment and then disappear
  const seoReady = !!storeInfo;
  // When storeInfo is undefined this expression is false, i.e. "SEO is enabled".
  // That is safe ONLY because seoReady below suppresses the whole <nav> until
  // /stores/info arrives. Do not rely on seoOff alone to hide seo-only items.
  const seoOff = storeInfo?.features?.seoEnabled === false;
  const hideDispensaries = menuAvailability?.dispensaries === false;
  const hideDelivery = menuAvailability?.deliveryAreas === false;
  const isLinkVisible = (item) => !(seoOff && item.seoOnly) && !(hideDelivery && item.to === '/weed-delivery');
  const visibleLinks = links.filter(isLinkVisible);
  const visibleLinkWrap = linkWrap.filter(isLinkVisible);

  // Toggle no-scroll class
  const toggleMenu = () => {
    setClicked(!clicked);
    document.body.classList.remove('no-scroll');
  };
  useEffect(() => {
    clicked && document.body.classList.add('no-scroll');
  });

  // Toggle no-scroll class if route changes to cover edge cases
  const history = useHistory();
  useEffect(() => {
    return history.listen(() => {
      document.body.classList.remove('no-scroll');
    });
  }, [history]);

  // Get Login Status for app from local storage
  const LoginStatus = JSON.parse(localStorage.getItem('LoginStatus'));

  const renderLink = (item, className) =>
    item.href ? (
      <a key={item.text} className={className} href={item.href} onClick={toggleMenu}>
        {item.text}
      </a>
    ) : (
      <Link key={item.text} className={className} to={item.to} onClick={toggleMenu}>
        {item.text}
      </Link>
    );

  const renderSection = (section, list, type, key, overrides) =>
    section?.visible && !(seoOff && section.seoOnly) && list && list.length ? (
      <MobileMainMenuCardUniversal
        key={key}
        heading={section.heading}
        list={list}
        type={type}
        toggleMenu={toggleMenu}
        {...cardProps}
        {...overrides}
      />
    ) : (
      ''
    );

  return (
    <>
      <StyledMobileMenuUniversalButton onClick={toggleMenu} colorButton={menuConfig.colorButton || props.colorButton}>
        <SVGIcon name={icons.menu || 'menu'} />
        <span className="t-hidden">open menu</span>
      </StyledMobileMenuUniversalButton>

      <StyledMobileMenuUniversal data-menu-state={clicked && seoReady} themeProps={styling}>
        {seoReady && (
        <nav className="mobile-menu__nav">
          <div className="mobile-menu__nav__header">
            <button className="mobile-menu__close" onClick={toggleMenu}>
              <SVGIcon name={icons.close || 'close'} />
              <span className="t-hidden">close menu</span>
            </button>
          </div>
          <div className={`mobile-menu__nav__body ${classNames.body || ''}`}>
            <Link
              className={`mobile-menu__shop-all ${classNames.shopAll || ''}`}
              to={shopAll.to || '/shop'}
              onClick={toggleMenu}
            >
              {shopAll.text || 'SHOP ALL'}
              {shopAll.icon && <SVGIcon className="up-arrow-icon" name={shopAll.icon} />}
            </Link>

            {renderSection(sections.discover, discoversAll, 'discover', 'discover')}
            {renderSection(sections.category, categories && categories.slice(0, 9), 'category', 'category')}
            {renderSection(sections.personality, personality, 'by_personality', 'personality')}
            {renderSection(sections.potency, thc, 'thc', 'thc')}

            {sections.brands?.visible && featuredBrand && featuredBrand.length
              ? featuredBrand.map(
                  (item) =>
                    item.brands.length > 0 && (
                      <MobileMainMenuCardUniversal
                        key={item.id}
                        heading={sections.brands.heading}
                        list={item.brands}
                        type="brand"
                        toggleMenu={toggleMenu}
                        {...cardProps}
                      />
                    )
                )
              : ''}

            {renderSection(!hideDispensaries && sections.dispensaries, pickupLocations, 'dispensary', 'dispensaries', {
              trailingLink: null,
            })}

            {visibleLinks.map((item) =>
              renderLink(item, `mobile-menu__shop-all ${item.className || classNames.link || ''}`)
            )}

            {visibleLinkWrap.length > 0 && (
              <div className="mobile-menu__link-wrap">
                {visibleLinkWrap.map((item) =>
                  renderLink(item, `mobile-menu__shop-all ${item.className || classNames.link || ''}`)
                )}
              </div>
            )}

            <div className={`mobile-menu__nav__urls ${classNames.urlWrap || ''}`}>
              {storeInfo?.features?.rewardsEnable && accountLinks.rewards && (
                <Link
                  onClick={toggleMenu}
                  to={accountLinks.rewards.to}
                  className={accountLinks.rewards.className || classNames.url}
                >
                  {accountLinks.rewards.text}
                </Link>
              )}

              {LoginStatus ? (
                <>
                  {(accountLinks.loggedIn || []).map((item) => renderLink(item, item.className || classNames.url))}
                  <button className={classNames.url} onClick={() => dispatch(authActions.logOut())}>
                    {accountLinks.logOutText || 'Log out'}
                  </button>
                </>
              ) : (
                <>{(accountLinks.loggedOut || []).map((item) => renderLink(item, item.className || classNames.url))}</>
              )}
            </div>
          </div>

          {(footer.heading || footer.socials?.length || footer.logo?.icon) && (
            <div className="mobile-menu__nav__footer">
              {footer.heading && (
                <h3 className={`mobile-menu__nav__footer__heading ${classNames.heading || ''}`}>{footer.heading}</h3>
              )}

              {footer.socials?.length > 0 && (
                <ul className="mobile-menu__nav__footer__socials">
                  {footer.socials.map((item) => (
                    <li className="mobile-menu__nav__footer__social" key={item.icon}>
                      <a href={item.href} className={classNames.social || ''}>
                        <span className="t-hidden">{item.label}</span>
                        <SVGIcon name={item.icon} />
                      </a>
                    </li>
                  ))}
                </ul>
              )}

              {footer.logo?.icon && <SVGIcon name={footer.logo.icon} className="mobile-menu__nav__footer__logo" />}
            </div>
          )}
        </nav>
        )}

        <div className="mobile-menu__background" onClick={toggleMenu} />
      </StyledMobileMenuUniversal>
    </>
  );
});

MobileMenuUniversal.propTypes = {
  handleOpenLocation: PropTypes.func,
  featuredBrand: PropTypes.array,
  discoversAll: PropTypes.array,
  colorButton: PropTypes.bool,
  pickupLocations: PropTypes.array,
  storeInfo: PropTypes.object,
  dispatch: PropTypes.func,
  menuAvailability: PropTypes.object,
};

export default MobileMenuUniversal;
